import { useEffect, useState } from "react";
import SubmitButton from "../SubmitButton";
import Modal from "./Modal";
import { useAuth } from "src/contexts/AuthContext";
import { useUrlParams } from "../../utils/url";
import { apiPost } from "src/utils/api";
import { Organization } from "src/shared/types";
import { ClipboardIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";
interface ApiKeyModalProps {
  onClose: () => void;
}

const ApiKeyModal = ({ onClose }: ApiKeyModalProps) => {
  const [ apiKey, setApiKey ] = useState<{ id: string, apiKey: string } | null>(null);
  const [ idCopied, setIdCopied ] = useState(false);
  const [ apiKeyCopied, setApiKeyCopied ] = useState(false);

  useEffect(() => {
    if (idCopied) {
      setTimeout(() => {
        setIdCopied(false);
      }, 2000);
    }
  }, [ idCopied ]);

  useEffect(() => {
    if (apiKeyCopied) {
      setTimeout(() => {
        setApiKeyCopied(false);
      }, 2000);
    }
  }, [ apiKeyCopied ]);

  return (
    <Modal title="Generate API Key" onClose={onClose}>
      { apiKey ?
        (
          <div className="flex flex-col items-center space-y-6">
            <div className="w-full">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                API Key ID
              </label>
              <div className="text-sm bg-gray-100 border border-gray-300 rounded-md px-4 py-3 text-center font-mono break-all select-all flex items-center justify-between">
                {apiKey.id}
                <div className="flex items-center gap-2">
                  {idCopied && (
                    <span className="text-xs text-green-600 font-medium text-nowrap">Copied!</span>
                  )}
                  <ClipboardIcon className={twMerge("w-4 h-4 cursor-pointer stroke-2", idCopied && "text-green-500")}  onClick={(e) => {
                    navigator.clipboard.writeText(apiKey.id);
                    setIdCopied(true);
                  }} />
                </div>
              </div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 pt-4">
                API Key
              </label>
              <div className="mx-auto text-sm bg-gray-100 border border-gray-300 rounded-md px-4 py-3 text-center font-mono break-all select-all flex items-center justify-between">
                {apiKey.apiKey}
                <div className="flex items-center gap-2">
                  {apiKeyCopied && (
                    <span className="text-xs text-green-600 font-medium text-nowrap">Copied!</span>
                  )}
                  <ClipboardIcon className={twMerge("w-4 h-4 cursor-pointer stroke-2", apiKeyCopied && "text-green-500")}  onClick={(e) => {
                    navigator.clipboard.writeText(apiKey.apiKey);
                    setApiKeyCopied(true);
                  }} />
                </div>
              </div>
            </div>
            <div className="bg-yellow-100 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded-md text-sm w-full">
              <strong>Important:</strong> This API key will <span className="font-semibold">never be shown again</span>. Please copy and store it securely. If you lose it, you will need to generate a new key.
            </div>
          </div>
        ) : (
          <FormContent setApiKey={setApiKey} />
        )
      }
    </Modal>
  );
};

interface FormContentProps {
  setApiKey: (apiKey: { id: string, apiKey: string } | null) => void;
}

const FormContent = ({ setApiKey }: FormContentProps) => {
  const { organizations } = useAuth();
  const params = useUrlParams();
  const org_id = params.org_id;
  const organization = organizations.find((org: Organization) => org.id === Number(org_id));

  const [ apiKeyName, setApiKeyName ] = useState("");
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState("");

  const handleGenerateApiKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization || !apiKeyName) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await apiPost(`/api/${organization.id}/generate-api-key`, { name: apiKeyName });
      const data = await response.json();

      if (!data.error) {
        setApiKey({ id: data.id, apiKey: data.api_key });
      } else {
        setApiKey(null);
        setError(data.error);
      }
    } catch (_) {
      setApiKey(null);
      setError("Failed to generate API key");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="space-y-3 sm:space-y-4">
      <div>
        {error && (
          <>
            <div className="p-2 sm:p-3 bg-red-50 border border-red-200 rounded-md mb-4 flex items-center justify-between">
              <p className="text-xs sm:text-sm text-red-600">{error}</p>
              <XMarkIcon className="w-4 h-4 cursor-pointer" onClick={() => setError("")} />
            </div>
          </>
        )}
        <label htmlFor="api_key_name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
          API Key Name
        </label>
        <input
          type="text"
          id="api_key_name"
          name="api_key_name"
          value={apiKeyName}
          onChange={(e) => setApiKeyName(e.target.value)}
          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          placeholder="API Key Name"
        />
      </div>

      <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-3 sm:pt-4">
        <SubmitButton
          loading={loading}
          loadingText="Generating API Key..."
          text="Generate API Key"
          onClick={handleGenerateApiKey}
          disabled={!apiKeyName.trim()}
        />
      </div>
    </form>
  );
};

export default ApiKeyModal;
